import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { useAuth } from './context/AuthContext';
import { isSuperAdmin } from './utils/superAdmin';
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  ButtonGroup,
  Row,
  Col,
  Card,
  Tabs,
  Tab
} from 'react-bootstrap';
import SignaturePad from './components/SignaturePad';

/**
 * 請款單管理組件
 * 提供請款單的新增、查看、簽核功能
 */
function ReimburseManagement() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  // RWD 狀態
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // 監聽視窗大小變化
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 狀態管理
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal 狀態
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [currentDetail, setCurrentDetail] = useState(null);

  // 新增請款單表單
  const [newRequest, setNewRequest] = useState({
    ssid: user?.e_ssid || '',
    term_id: '',
    requester_id: user?.e_id || '',
    memo: '',
    items: [{ s_id: '', description: '', amount_requested: 0 }]
  });

  // 科目列表
  const [subjects, setSubjects] = useState([]);

  // 社別列表
  const [societies, setSocieties] = useState([]);

  // 屆別列表
  const [terms, setTerms] = useState([]);

  // 員工列表
  const [employees, setEmployees] = useState([]);

  // 開啟新增請款單彈窗
  const openNewRequestModal = () => {
    // 重新初始化表單，確保帶入登入者資訊
    setNewRequest({
      ssid: user?.e_ssid || '',
      term_id: '',
      requester_id: user?.e_id || '',
      requester_search: '',
      requester_dropdown_open: false,
      memo: '',
      items: [{ s_id: '', description: '', amount_requested: 0 }]
    });
    
    // 載入登入者社別的屆別和員工
    if (user?.e_ssid) {
      loadTerms(user.e_ssid);
      loadEmployees(user.e_ssid);
    }
    
    setShowModal(true);
  };

  // 上傳收據相關狀態
  const [uploadingItemId, setUploadingItemId] = useState(null);

  // 預覽收據彈窗
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // 線上簽名相關狀態
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [signatureItemId, setSignatureItemId] = useState(null);

  // 開啟預覽
  const openPreview = (attachment) => {
    setPreviewFile(attachment);
    setShowPreviewModal(true);
  };

  // 開啟簽名板
  const openSignaturePad = (item_id) => {
    setSignatureItemId(item_id);
    setShowSignaturePad(true);
  };

  // 儲存簽名（簽名板回傳的 File 物件）
  const handleSaveSignature = async (signatureFile) => {
    if (!signatureItemId) return;

    // 使用現有的 uploadReceipt 函數上傳簽名檔案
    await uploadReceipt(signatureItemId, signatureFile);

    // 重置狀態
    setSignatureItemId(null);
  };

  // 上傳收據
  const uploadReceipt = async (item_id, file) => {
    try {
      setUploadingItemId(item_id);
      setError(null);

      const formData = new FormData();
      formData.append('receipt', file);
      formData.append('item_id', item_id);
      formData.append('uploaded_by', user?.e_id);

      const response = await axios.post('/finance/reimburse/upload-receipt', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSuccess('收據上傳成功！');
        // 重新載入請款單詳情以顯示新上傳的附件
        await viewDetail(currentDetail.req_id);
      }
    } catch (err) {
      setError(err.response?.data?.error || '上傳收據失敗');
    } finally {
      setUploadingItemId(null);
    }
  };

  // 刪除收據
  const deleteReceipt = async (fileId) => {
    try {
      if (!window.confirm('確定要刪除此收據嗎？')) {
        return;
      }

      const response = await axios.delete(`/finance/reimburse/receipt/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('收據刪除成功');
        // 重新載入請款單詳情
        await viewDetail(currentDetail.req_id);
      }
    } catch (err) {
      setError(err.response?.data?.error || '刪除收據失敗');
    }
  };

  // 篩選條件
  const [filterStatus, setFilterStatus] = useState('all');

  // 載入社別列表
  const loadSocieties = async () => {
    try {
      const response = await axios.get('/stores', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setSocieties(response.data);
      }
    } catch (err) {
      console.error('載入社別列表失敗:', err);
    }
  };

  // 載入屆別列表
  const loadTerms = async (ssid) => {
    try {
      if (!ssid) {
        setTerms([]);
        return;
      }

      const response = await axios.get('/finance/terms', {
        params: { ssid },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const termsData = response.data.data;
        setTerms(termsData);
        
        // 新增請款單時，自動選擇啟用的屆別
        if (termsData.length > 0 && newRequest.ssid && !newRequest.term_id) {
          // 優先選擇 is_active = 1 的屆別
          const activeTerm = termsData.find(term => term.is_active === 1 || term.is_active === '1');
          if (activeTerm) {
            console.log('請款單 - 自動選擇啟用屆別:', activeTerm.term_name);
            setNewRequest(prev => ({ ...prev, term_id: activeTerm.term_id }));
          } else {
            // 如果沒有啟用的屆別，選擇最新的（第一個）
            console.log('請款單 - 沒有啟用屆別，選擇第一個:', termsData[0].term_name);
            setNewRequest(prev => ({ ...prev, term_id: termsData[0].term_id }));
          }
        }
      }
    } catch (err) {
      console.error('載入屆別列表失敗:', err);
    }
  };

  // 載入員工列表（社別下的社友 group=5 或秘書 group=3）
  const loadEmployees = async (ssid) => {
    try {
      if (!ssid) {
        setEmployees([]);
        return;
      }

      const response = await axios.get('/employees', {
        params: {
          filter: '',
          limit: 500
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.data) {
        // 篩選出指定社別且（group=5 的社友 或 group=3 的秘書）
        const filteredEmployees = response.data.data.filter(
          emp => emp.e_ssid === parseInt(ssid) && (emp.e_group === 5 || emp.e_group === 3)
        );
        console.log(`載入社別 ${ssid} 的員工:`, filteredEmployees.length, '人');
        console.log('員工列表:', filteredEmployees.map(e => `${e.e_name}(${e.e_id})`));
        setEmployees(filteredEmployees);
      }
    } catch (err) {
      console.error('載入員工列表失敗:', err);
      setEmployees([]);
    }
  };

  // 載入科目列表（按社別篩選）
  const loadSubjects = async (ssid = null) => {
    try {
      const params = { active_only: 'true' };
      // 如果指定了 ssid，只載入該社的科目
      if (ssid) {
        params.ssid = ssid;
      }

      const response = await axios.get('/finance/subjects', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSubjects(response.data.data);
      }
    } catch (err) {
      console.error('載入科目列表失敗:', err);
    }
  };

  // 載入請款單列表
  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (user?.e_position !== 17 && !isSuperAdmin(user?.e_id)) {
        // 非秘書/管理員，只顯示自己的請款單
        params.requester_id = user?.e_id;
      }

      const response = await axios.get('/finance/reimburse', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setRequests(response.data.data);
      }
    } catch (err) {
      setError('載入請款單列表失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 初始化載入
  useEffect(() => {
    loadSocieties();
    loadRequests();

    // 如果表單已有預設社別，載入對應的屆別、員工和科目
    if (newRequest.ssid) {
      loadTerms(newRequest.ssid);
      loadEmployees(newRequest.ssid);
      loadSubjects(newRequest.ssid);
    } else {
      // 管理員可以看到所有科目
      loadSubjects();
    }
  }, [filterStatus]);

  // 當社別改變時載入對應的屆別、員工和科目
  useEffect(() => {
    if (newRequest.ssid) {
      // 清空屆別選擇，讓 loadTerms 自動選擇啟用屆別
      setNewRequest(prev => ({ ...prev, term_id: '' }));
      loadTerms(newRequest.ssid);
      loadEmployees(newRequest.ssid);
      loadSubjects(newRequest.ssid);
    }
  }, [newRequest.ssid]);

  // 點擊外部關閉申請人下拉選單
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (newRequest.requester_dropdown_open && !event.target.closest('.requester-autocomplete')) {
        setNewRequest(prev => ({ ...prev, requester_dropdown_open: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [newRequest.requester_dropdown_open]);

  // 新增明細行
  const addItem = () => {
    setNewRequest({
      ...newRequest,
      items: [...newRequest.items, { s_id: '', description: '', amount_requested: 0 }]
    });
  };

  // 刪除明細行
  const removeItem = (index) => {
    const items = [...newRequest.items];
    items.splice(index, 1);
    setNewRequest({ ...newRequest, items });
  };

  // 更新明細行
  const updateItem = (index, field, value) => {
    const items = [...newRequest.items];
    items[index][field] = value;
    setNewRequest({ ...newRequest, items });
  };

  // 計算總金額
  const calculateTotal = () => {
    return newRequest.items.reduce((sum, item) => sum + (parseFloat(item.amount_requested) || 0), 0);
  };

  // 提交新增請款單
  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);

      // 驗證
      if (!newRequest.ssid || !newRequest.term_id || !newRequest.requester_id) {
        setError('社別、屆別和申請人為必填欄位');
        return;
      }

      if (newRequest.items.length === 0 || !newRequest.items[0].s_id) {
        setError('至少需要一項請款明細');
        return;
      }

      const response = await axios.post('/finance/reimburse', newRequest, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('請款單新增成功！');
        setShowModal(false);
        setNewRequest({
          ssid: user?.e_ssid || '',
          term_id: '',
          requester_id: '',
          memo: '',
          items: [{ s_id: '', description: '', amount_requested: 0 }]
        });
        loadRequests();
      }
    } catch (err) {
      setError(err.response?.data?.error || '新增請款單失敗');
    }
  };

  // 查看請款單詳情
  const viewDetail = async (req_id) => {
    try {
      const response = await axios.get(`/finance/reimburse/${req_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log('請款單詳情資料:', response.data.data);
        console.log('付款記錄數量:', response.data.data.payments?.length || 0);
        setCurrentDetail(response.data.data);
        // 初始化核准金額（預設為申請金額）
        initializeApprovalAmounts(response.data.data.items);
        setShowDetailModal(true);
      }
    } catch (err) {
      setError('載入請款單詳情失敗：' + (err.response?.data?.error || err.message));
    }
  };

  // 核准金額輸入狀態
  const [approvalAmounts, setApprovalAmounts] = useState({});
  const [approvalMemo, setApprovalMemo] = useState('');

  // 付款資訊彈窗狀態
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState({
    paid_amount: 0,
    payment_method: 'transfer',
    payment_memo: '',
    receipt_number: ''
  });

  // 初始化核准金額（與申請金額相同）
  const initializeApprovalAmounts = (items) => {
    const amounts = {};
    items?.forEach(item => {
      amounts[item.item_id] = item.amount_requested;
    });
    setApprovalAmounts(amounts);
  };

  // 更新單項核准金額
  const updateApprovalAmount = (item_id, amount) => {
    setApprovalAmounts(prev => ({
      ...prev,
      [item_id]: parseFloat(amount) || 0
    }));
  };

  // 更新請款單狀態
  const updateStatus = async (req_id, status, payment_data = null) => {
    try {
      console.log('🔄 updateStatus 被調用:', { req_id, status, payment_data });
      setError(null);
      setSuccess(null);

      // 如果是核准，準備核准金額資料
      let approved_items = null;
      if (status === 'approved') {
        approved_items = currentDetail.items.map(item => ({
          item_id: item.item_id,
          amount_approved: approvalAmounts[item.item_id] || item.amount_requested
        }));
      }

      // 準備請求資料
      const requestData = {
        status,
        approver_id: user?.e_id,
        approval_memo: approvalMemo || null,
        approved_items
      };

      // 如果是付款，加入付款資訊
      if (status === 'paid' && payment_data) {
        requestData.payment_info = payment_data;
        console.log('📋 付款請求資料:', requestData);
      }

      console.log('📤 發送 PUT 請求到:', `/finance/reimburse/${req_id}/status`);
      const response = await axios.put(`/finance/reimburse/${req_id}/status`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('✅ API 回應:', response.data);
      
      if (response.data.success) {
        setSuccess(`請款單狀態已更新為: ${status}`);
        setShowDetailModal(false);
        setShowPaymentModal(false);
        setApprovalMemo('');
        setApprovalAmounts({});
        setPaymentInfo({
          paid_amount: 0,
          payment_method: 'transfer',
          payment_memo: '',
          receipt_number: ''
        });
        loadRequests();
      }
    } catch (err) {
      setError(err.response?.data?.error || '更新狀態失敗');
    }
  };

  // 開啟付款彈窗
  const openPaymentModal = () => {
    // 初始化付款金額為核准金額
    setPaymentInfo({
      paid_amount: currentDetail.total_approved || 0,
      payment_method: 'transfer',
      payment_memo: '',
      receipt_number: ''
    });
    setShowPaymentModal(true);
  };

  // 執行付款
  const handlePayment = async () => {
    try {
      console.log('💰 handlePayment 被調用');
      console.log('paymentInfo:', paymentInfo);
      console.log('currentDetail:', currentDetail);
      
      if (paymentInfo.paid_amount <= 0) {
        setError('付款金額必須大於 0');
        console.log('❌ 付款金額無效:', paymentInfo.paid_amount);
        return;
      }

      const payment_data = {
        ...paymentInfo,
        payer_id: user?.e_id
      };

      console.log('📤 準備發送付款請求:', payment_data);
      await updateStatus(currentDetail.req_id, 'paid', payment_data);
    } catch (err) {
      console.error('❌ 付款失敗:', err);
      setError(err.response?.data?.error || '付款失敗');
    }
  };

  // 刪除請款單
  const deleteRequest = async (req_id) => {
    try {
      if (!window.confirm('確定要刪除此請款單嗎？此操作無法復原！')) {
        return;
      }

      setError(null);
      setSuccess(null);

      const response = await axios.delete(`/finance/reimburse/${req_id}`, {
        data: {
          user_id: user?.e_id,
          user_position: user?.e_position
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('請款單刪除成功');
        setShowDetailModal(false);
        loadRequests();
      }
    } catch (err) {
      setError(err.response?.data?.error || '刪除失敗');
    }
  };

  // 取得狀態顏色
  const getStatusColor = (status) => {
    const colors = {
      draft: 'secondary',
      submitted: 'primary',
      approved: 'success',
      rejected: 'danger',
      paid: 'info',
      closed: 'dark'
    };
    return colors[status] || 'secondary';
  };

  // 取得狀態文字
  const getStatusText = (status) => {
    const texts = {
      draft: '草稿',
      submitted: '已送出',
      approved: '已核准',
      rejected: '已拒絕',
      paid: '已付款',
      closed: '已關閉'
    };
    return texts[status] || status;
  };

  // 格式化金額
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // 基本管理員權限（用於列表刪除等操作）
  const isAdmin = isSuperAdmin(user?.e_id) || user?.e_position === 17;

  // 職位常數
  const POSITION_PRESIDENT = 2;   // 社長
  const POSITION_FINANCE = 12;    // 財務長

  // 檢查是否可以簽核（超級管理員 e_id=80 或 當社社長）
  const canApprove = (requestDetail) => {
    if (!requestDetail) return false;
    // 超級管理員可以簽核所有請款單
    if (isSuperAdmin(user?.e_id)) return true;
    // 當社社長可以簽核本社的請款單
    if (user?.e_position === POSITION_PRESIDENT && user?.e_ssid === requestDetail.ssid) {
      return true;
    }
    return false;
  };

  // 檢查是否可以標記付款（超級管理員 e_id=80 或 當社財務長）
  const canMarkPaid = (requestDetail) => {
    if (!requestDetail) return false;
    // 超級管理員可以標記所有請款單為已付款
    if (isSuperAdmin(user?.e_id)) return true;
    // 當社財務長可以標記本社的請款單為已付款
    if (user?.e_position === POSITION_FINANCE && user?.e_ssid === requestDetail.ssid) {
      return true;
    }
    return false;
  };

  return (
    <Container fluid className={isMobile ? "py-2 px-2" : "py-4"}>
      <h2 className={isMobile ? "mb-3 fs-5" : "mb-4"}>
        💰 {isMobile ? "請款單管理" : "財務管理 - 請款單管理"}
      </h2>

      {/* 錯誤/成功訊息 */}
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      {/* 篩選工具列 */}
      {isMobile ? (
        /* 手機版篩選工具列 */
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Select
              size="sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ width: 'auto', minWidth: '120px' }}
            >
              <option value="all">📋 全部</option>
              <option value="draft">📝 草稿</option>
              <option value="submitted">📤 已送出</option>
              <option value="approved">✅ 已核准</option>
              <option value="paid">💰 已付款</option>
            </Form.Select>
            <Button variant="primary" size="sm" onClick={openNewRequestModal}>
              ➕ 新增
            </Button>
          </div>
        </div>
      ) : (
        /* 桌面版篩選工具列 */
        <div className="d-flex justify-content-between align-items-center mb-3">
          <ButtonGroup>
            <Button
              variant={filterStatus === 'all' ? 'primary' : 'outline-primary'}
              onClick={() => setFilterStatus('all')}
            >
              全部
            </Button>
            <Button
              variant={filterStatus === 'draft' ? 'secondary' : 'outline-secondary'}
              onClick={() => setFilterStatus('draft')}
            >
              草稿
            </Button>
            <Button
              variant={filterStatus === 'submitted' ? 'primary' : 'outline-primary'}
              onClick={() => setFilterStatus('submitted')}
            >
              已送出
            </Button>
            <Button
              variant={filterStatus === 'approved' ? 'success' : 'outline-success'}
              onClick={() => setFilterStatus('approved')}
            >
              已核准
            </Button>
            <Button
              variant={filterStatus === 'paid' ? 'info' : 'outline-info'}
              onClick={() => setFilterStatus('paid')}
            >
              已付款
            </Button>
          </ButtonGroup>

          <Button variant="primary" onClick={openNewRequestModal}>
            ➕ 新增請款單
          </Button>
        </div>
      )}

      {/* 請款單列表 */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : isMobile ? (
        /* 手機版卡片列表 */
        <div>
          {requests.length === 0 ? (
            <div className="text-center text-muted py-4">
              目前沒有請款單資料
            </div>
          ) : (
            requests.map(req => (
              <Card key={req.req_id} className="mb-3 shadow-sm">
                <Card.Body className="p-3">
                  {/* 頂部：單號 + 狀態 */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <code style={{ fontSize: '14px' }}>#{req.req_id}</code>
                      <span className="text-muted ms-2" style={{ fontSize: '12px' }}>
                        {new Date(req.request_date).toLocaleDateString('zh-TW')}
                      </span>
                    </div>
                    <Badge bg={getStatusColor(req.status)}>
                      {getStatusText(req.status)}
                    </Badge>
                  </div>

                  {/* 申請人 + 明細數 */}
                  <div className="mb-2">
                    <strong>{req.requester_name}</strong>
                    <Badge bg="secondary" className="ms-2">{req.item_count} 項</Badge>
                  </div>

                  {/* 金額資訊 */}
                  <Row className="mb-2">
                    <Col xs={6}>
                      <small className="text-muted">申請金額</small>
                      <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                        {formatAmount(req.total_requested)}
                      </div>
                    </Col>
                    <Col xs={6}>
                      <small className="text-muted">核准金額</small>
                      <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#28a745' }}>
                        {formatAmount(req.total_approved)}
                      </div>
                    </Col>
                  </Row>

                  {/* 操作按鈕 */}
                  <div className="d-flex gap-2 mt-2 pt-2 border-top">
                    <Button
                      variant="primary"
                      size="sm"
                      className="flex-grow-1"
                      onClick={() => viewDetail(req.req_id)}
                    >
                      📋 查看詳情
                    </Button>
                    {/* 管理員可以刪除任何請款單 */}
                    {isAdmin && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => deleteRequest(req.req_id)}
                      >
                        🗑️
                      </Button>
                    )}
                    {/* 一般使用者只能刪除自己的草稿 */}
                    {!isAdmin && req.status === 'draft' && req.requester_id === user?.e_id && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => deleteRequest(req.req_id)}
                      >
                        🗑️
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            ))
          )}
        </div>
      ) : (
        /* 桌面版表格 */
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th style={{ width: '80px' }}>單號</th>
              <th style={{ width: '100px' }}>申請日期</th>
              <th>申請人</th>
              <th style={{ width: '120px' }}>申請金額</th>
              <th style={{ width: '120px' }}>核准金額</th>
              <th style={{ width: '100px' }}>狀態</th>
              <th style={{ width: '80px' }}>明細</th>
              <th style={{ width: '150px' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center text-muted py-4">
                  目前沒有請款單資料
                </td>
              </tr>
            ) : (
              requests.map(req => (
                <tr key={req.req_id}>
                  <td><code>{req.req_id}</code></td>
                  <td>{new Date(req.request_date).toLocaleDateString('zh-TW')}</td>
                  <td>{req.requester_name}</td>
                  <td className="text-end">{formatAmount(req.total_requested)}</td>
                  <td className="text-end">{formatAmount(req.total_approved)}</td>
                  <td>
                    <Badge bg={getStatusColor(req.status)}>
                      {getStatusText(req.status)}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <Badge bg="info">{req.item_count} 項</Badge>
                  </td>
                  <td>
                    <ButtonGroup size="sm">
                      <Button
                        variant="outline-primary"
                        onClick={() => viewDetail(req.req_id)}
                      >
                        查看
                      </Button>
                      {/* 管理員可以刪除任何請款單 */}
                      {isAdmin && (
                        <Button
                          variant="outline-danger"
                          onClick={() => deleteRequest(req.req_id)}
                        >
                          🗑️
                        </Button>
                      )}
                      {/* 一般使用者只能刪除自己的草稿 */}
                      {!isAdmin && req.status === 'draft' && req.requester_id === user?.e_id && (
                        <Button
                          variant="outline-danger"
                          onClick={() => deleteRequest(req.req_id)}
                        >
                          🗑️
                        </Button>
                      )}
                    </ButtonGroup>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* 新增請款單 Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>新增請款單</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>社別 <span className="text-danger">*</span></Form.Label>
                  {isAdmin ? (
                    <Form.Select
                      value={newRequest.ssid}
                      onChange={(e) => setNewRequest({ ...newRequest, ssid: e.target.value })}
                    >
                      <option value="">請選擇社別</option>
                      {societies.map(society => (
                        <option key={society.ss_id} value={society.ss_id}>
                          {society.ss_name}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      value={societies.find(s => s.ss_id === newRequest.ssid)?.ss_name || ''}
                      readOnly
                      disabled
                    />
                  )}
                  {!isAdmin && (
                    <Form.Text className="text-muted">
                      社別預設為您的社別
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>屆別 <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={newRequest.term_id}
                    onChange={(e) => setNewRequest({ ...newRequest, term_id: e.target.value })}
                    disabled={!newRequest.ssid}
                  >
                    <option value="">請選擇屆別</option>
                    {terms.map(term => (
                      <option key={term.term_id} value={term.term_id}>
                        {term.term_name}（第 {term.term_no} 屆）
                      </option>
                    ))}
                  </Form.Select>
                  {!newRequest.ssid && (
                    <Form.Text className="text-muted">
                      請先選擇社別
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3 requester-autocomplete" style={{ position: 'relative' }}>
                  <Form.Label>申請人 <span className="text-danger">*</span></Form.Label>
                  {isAdmin ? (
                    <>
                      <Form.Control
                        type="text"
                        placeholder="輸入姓名搜尋..."
                        value={newRequest.requester_id ? 
                          (employees.find(e => e.e_id === newRequest.requester_id)?.e_name || '') : 
                          (newRequest.requester_search || '')
                        }
                        onChange={(e) => {
                          const searchText = e.target.value;
                          setNewRequest({ ...newRequest, requester_search: searchText, requester_id: '', requester_dropdown_open: true });
                        }}
                        onFocus={() => setNewRequest({ ...newRequest, requester_dropdown_open: true })}
                        disabled={!newRequest.ssid}
                        autoComplete="off"
                      />
                      {newRequest.requester_dropdown_open && newRequest.ssid && (
                        <div 
                          style={{
                            position: 'absolute',
                            zIndex: 1000,
                            width: '100%',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: 'white',
                            border: '1px solid #ced4da',
                            borderRadius: '0.25rem',
                            marginTop: '2px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        >
                          {employees
                            .filter(emp => 
                              !newRequest.requester_search || 
                              emp.e_name.toLowerCase().includes((newRequest.requester_search || '').toLowerCase())
                            )
                            .slice(0, 10)
                            .map(emp => (
                              <div
                                key={emp.e_id}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  backgroundColor: newRequest.requester_id === emp.e_id ? '#e7f3ff' : 'white'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = newRequest.requester_id === emp.e_id ? '#e7f3ff' : 'white'}
                                onClick={() => {
                                  setNewRequest({ 
                                    ...newRequest, 
                                    requester_id: emp.e_id,
                                    requester_search: emp.e_name,
                                    requester_dropdown_open: false
                                  });
                                }}
                              >
                                {emp.e_name}
                              </div>
                            ))}
                          {employees.filter(emp => 
                            !newRequest.requester_search || 
                            emp.e_name.toLowerCase().includes((newRequest.requester_search || '').toLowerCase())
                          ).length === 0 && (
                            <div style={{ padding: '8px 12px', color: '#6c757d' }}>
                              找不到符合的申請人
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <Form.Control
                      type="text"
                      value={user?.e_name || ''}
                      readOnly
                      disabled
                    />
                  )}
                  {!newRequest.ssid && isAdmin && (
                    <Form.Text className="text-muted">
                      請先選擇社別
                    </Form.Text>
                  )}
                  {!isAdmin && (
                    <Form.Text className="text-muted">
                      申請人預設為您自己
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>備註</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newRequest.memo}
                onChange={(e) => setNewRequest({ ...newRequest, memo: e.target.value })}
              />
            </Form.Group>

            <hr />
            <h5>請款明細</h5>

            {newRequest.items.map((item, index) => (
              <Card key={index} className="mb-2">
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>科目 <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          value={item.s_id}
                          onChange={(e) => updateItem(index, 's_id', e.target.value)}
                        >
                          <option value="">選擇科目</option>
                          {subjects.filter(s => s.s_type === 'expense').map(s => (
                            <option key={s.s_id} value={s.s_id}>
                              {s.s_code} - {s.s_title}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group>
                        <Form.Label>說明</Form.Label>
                        <Form.Control
                          type="text"
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>金額</Form.Label>
                        <Form.Control
                          type="number"
                          value={item.amount_requested}
                          onChange={(e) => updateItem(index, 'amount_requested', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                      {index > 0 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeItem(index)}
                        >
                          ✕
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}

            <Button variant="outline-secondary" size="sm" onClick={addItem}>
              ➕ 新增明細
            </Button>

            <div className="mt-3 text-end">
              <h5>總金額: {formatAmount(calculateTotal())}</h5>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            新增
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 請款單詳情 Modal */}
      {currentDetail && (
        <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>請款單詳情 #{currentDetail.req_id}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <strong>申請人:</strong> {currentDetail.requester_name}<br />
                <strong>申請日期:</strong> {new Date(currentDetail.request_date).toLocaleDateString('zh-TW')}<br />
                <strong>狀態:</strong> <Badge bg={getStatusColor(currentDetail.status)}>{getStatusText(currentDetail.status)}</Badge>
              </Col>
              <Col md={6}>
                <strong>申請金額:</strong> {formatAmount(currentDetail.total_requested)}<br />
                <strong>核准金額:</strong> {formatAmount(currentDetail.total_approved)}<br />
              </Col>
            </Row>

            {currentDetail.memo && (
              <Alert variant="info">
                <strong>備註:</strong> {currentDetail.memo}
              </Alert>
            )}

            <h5>請款明細</h5>
            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th>科目</th>
                  <th>說明</th>
                  <th>申請金額</th>
                  {currentDetail.status === 'submitted' && canApprove(currentDetail) && <th>核准金額</th>}
                  {currentDetail.status !== 'submitted' && <th>核准金額</th>}
                  <th>收據</th>
                </tr>
              </thead>
              <tbody>
                {currentDetail.items?.map((item, index) => {
                  // 找出此明細的附件
                  const itemAttachments = currentDetail.attachments?.filter(
                    att => att.owner_type === 'reimb_item' && att.owner_id === item.item_id
                  ) || [];
                  
                  return (
                    <tr key={index}>
                      <td>{item.s_code} - {item.s_title}</td>
                      <td>{item.description}</td>
                      <td className="text-end">{formatAmount(item.amount_requested)}</td>
                      {currentDetail.status === 'submitted' && canApprove(currentDetail) && (
                        <td>
                          <Form.Control
                            type="number"
                            size="sm"
                            value={approvalAmounts[item.item_id] || item.amount_requested}
                            onChange={(e) => updateApprovalAmount(item.item_id, e.target.value)}
                            style={{ width: '120px' }}
                          />
                        </td>
                      )}
                      {currentDetail.status !== 'submitted' && (
                        <td className="text-end">{formatAmount(item.amount_approved)}</td>
                      )}
                      <td>
                        {/* 顯示已上傳的收據 */}
                        {itemAttachments.length > 0 ? (
                          <div className="d-flex flex-wrap gap-2">
                            {itemAttachments.map((att, idx) => (
                              <div key={idx} style={{ position: 'relative', display: 'inline-block' }}>
                                {/* 收據縮圖 */}
                                {att.mime_type && att.mime_type.startsWith('image/') ? (
                                  <img
                                    src={`/${att.file_path}`}
                                    alt={`收據 ${idx + 1}`}
                                    style={{
                                      width: '80px',
                                      height: '100px',
                                      objectFit: 'cover',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      border: '1px solid #dee2e6'
                                    }}
                                    onClick={() => openPreview(att)}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: '80px',
                                      height: '100px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: '#f8f9fa',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      border: '1px solid #dee2e6'
                                    }}
                                    onClick={() => openPreview(att)}
                                  >
                                    <div className="text-center">
                                      <div style={{ fontSize: '24px' }}>📄</div>
                                      <div style={{ fontSize: '10px' }}>PDF</div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* 刪除按鈕（右上角） - 未付款前都可刪除 */}
                                {['draft', 'submitted', 'approved'].includes(currentDetail.status) && (
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => deleteReceipt(att.file_id)}
                                    style={{
                                      position: 'absolute',
                                      top: '-5px',
                                      right: '-5px',
                                      padding: '2px 6px',
                                      fontSize: '12px',
                                      lineHeight: '1',
                                      borderRadius: '50%',
                                      minWidth: 'auto',
                                      width: '20px',
                                      height: '20px'
                                    }}
                                  >
                                    ✕
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">無</span>
                        )}
                        
                        {/* 上傳按鈕（未付款前都可上傳） */}
                        {['draft', 'submitted', 'approved'].includes(currentDetail.status) && (
                          <div className="mt-1 d-flex gap-2">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              style={{ display: 'none' }}
                              id={`receipt-upload-${item.item_id}`}
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  uploadReceipt(item.item_id, e.target.files[0]);
                                  e.target.value = ''; // 清空選擇
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => document.getElementById(`receipt-upload-${item.item_id}`).click()}
                              disabled={uploadingItemId === item.item_id}
                            >
                              {uploadingItemId === item.item_id ? '上傳中...' : '📤 上傳收據'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() => openSignaturePad(item.item_id)}
                              disabled={uploadingItemId === item.item_id}
                            >
                              ✍️ 線上簽名
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {currentDetail.status === 'submitted' && canApprove(currentDetail) && (
                  <tr>
                    <td colSpan="3" className="text-end"><strong>核准總額:</strong></td>
                    <td className="text-end">
                      <strong>
                        {formatAmount(Object.values(approvalAmounts).reduce((sum, amt) => sum + (parseFloat(amt) || 0), 0))}
                      </strong>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {currentDetail.approvals && currentDetail.approvals.length > 0 && (
              <>
                <h5 className="mt-3">簽核記錄</h5>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      <th>簽核人</th>
                      <th>動作</th>
                      <th>日期</th>
                      <th>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentDetail.approvals.map((approval, index) => (
                      <tr key={index}>
                        <td>{approval.approver_name}</td>
                        <td>
                          <Badge bg={approval.decision === 'approved' ? 'success' : 'danger'}>
                            {approval.decision === 'approved' ? '核准' : approval.decision === 'rejected' ? '拒絕' : '待審'}
                          </Badge>
                        </td>
                        <td>{approval.decided_at ? new Date(approval.decided_at).toLocaleString('zh-TW') : '-'}</td>
                        <td>{approval.comment || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {currentDetail.payments && currentDetail.payments.length > 0 && (
              <>
                <h5 className="mt-3">付款記錄</h5>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      <th>付款人</th>
                      <th>付款日期</th>
                      <th>付款金額</th>
                      <th>付款方式</th>
                      <th>憑證編號</th>
                      <th>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentDetail.payments.map((payment, index) => (
                      <tr key={index}>
                        <td>{payment.payer_name}</td>
                        <td>{payment.paid_at ? new Date(payment.paid_at).toLocaleString('zh-TW') : '-'}</td>
                        <td className="text-end">${payment.paid_amount?.toLocaleString()}</td>
                        <td>
                          <Badge bg="info">
                            {payment.payment_method === 'cash' ? '現金' : 
                             payment.payment_method === 'transfer' ? '轉帳' :
                             payment.payment_method === 'check' ? '支票' : '其他'}
                          </Badge>
                        </td>
                        <td>{payment.receipt_number || '-'}</td>
                        <td>{payment.payment_memo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {/* 簽核按鈕：超級管理員(e_id=80)或當社社長可見 */}
            {canApprove(currentDetail) && currentDetail.status === 'submitted' && (
              <>
                <div className="w-100 mb-3">
                  <Form.Group>
                    <Form.Label>審核備註</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="請輸入審核意見（選填）"
                      value={approvalMemo}
                      onChange={(e) => setApprovalMemo(e.target.value)}
                    />
                  </Form.Group>
                </div>
                <Button
                  variant="success"
                  onClick={() => updateStatus(currentDetail.req_id, 'approved')}
                >
                  ✓ 確認簽核
                </Button>
                <Button
                  variant="danger"
                  onClick={() => updateStatus(currentDetail.req_id, 'rejected')}
                >
                  ✕ 拒絕
                </Button>
              </>
            )}
            {/* 標記已付款按鈕：超級管理員(e_id=80)或當社財務長可見 */}
            {canMarkPaid(currentDetail) && currentDetail.status === 'approved' && (
              <Button
                variant="info"
                onClick={openPaymentModal}
              >
                💰 標記為已付款
              </Button>
            )}
            {currentDetail.status === 'draft' && currentDetail.requester_id === user?.e_id && (
              <Button
                variant="primary"
                onClick={() => updateStatus(currentDetail.req_id, 'submitted')}
              >
                送出簽核
              </Button>
            )}
            {/* 管理員或秘書可以刪除任何狀態的請款單 */}
            {isAdmin && (
              <Button
                variant="outline-danger"
                onClick={() => deleteRequest(currentDetail.req_id)}
              >
                🗑️ 刪除
              </Button>
            )}
            {/* 一般使用者只能刪除自己的草稿 */}
            {!isAdmin && currentDetail.status === 'draft' && currentDetail.requester_id === user?.e_id && (
              <Button
                variant="outline-danger"
                onClick={() => deleteRequest(currentDetail.req_id)}
              >
                🗑️ 刪除
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
              關閉
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* 付款資訊彈窗 */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title>付款資訊</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>付款金額 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={paymentInfo.paid_amount}
                onChange={(e) => setPaymentInfo({ ...paymentInfo, paid_amount: parseFloat(e.target.value) || 0 })}
                placeholder="請輸入付款金額"
              />
              <Form.Text className="text-muted">
                核准金額: ${currentDetail?.total_approved?.toLocaleString()}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>付款方式</Form.Label>
              <Form.Control
                type="text"
                value="轉帳"
                readOnly
                disabled
              />
              <Form.Text className="text-muted">
                付款日期為系統當下時間
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>收據/憑證編號</Form.Label>
              <Form.Control
                type="text"
                value={paymentInfo.receipt_number}
                onChange={(e) => setPaymentInfo({ ...paymentInfo, receipt_number: e.target.value })}
                placeholder="請輸入收據或憑證編號（選填）"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>付款備註</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={paymentInfo.payment_memo}
                onChange={(e) => setPaymentInfo({ ...paymentInfo, payment_memo: e.target.value })}
                placeholder="請輸入付款備註（選填）"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
            取消
          </Button>
          <Button variant="info" onClick={handlePayment}>
            💰 確認付款
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 收據預覽彈窗 */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>收據預覽</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewFile && (
            <>
              {/* 如果是圖片 */}
              {previewFile.mime_type && previewFile.mime_type.startsWith('image/') && (
                <img 
                  src={`/${previewFile.file_path}`} 
                  alt="收據預覽" 
                  style={{ maxWidth: '100%', maxHeight: '70vh' }}
                />
              )}
              {/* 如果是 PDF */}
              {previewFile.mime_type === 'application/pdf' && (
                <iframe
                  src={`/${previewFile.file_path}`}
                  title="PDF 預覽"
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                />
              )}
              {/* 其他類型顯示下載連結 */}
              {previewFile.mime_type && 
               !previewFile.mime_type.startsWith('image/') && 
               previewFile.mime_type !== 'application/pdf' && (
                <div className="p-4">
                  <p>無法預覽此檔案類型</p>
                  <a href={`/${previewFile.file_path}`} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                    開啟檔案
                  </a>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            關閉
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 線上簽名板 */}
      <SignaturePad
        show={showSignaturePad}
        onHide={() => {
          setShowSignaturePad(false);
          setSignatureItemId(null);
        }}
        onSave={handleSaveSignature}
      />
    </Container>
  );
}

export default ReimburseManagement;
